'use strict';

const { uniq, uniqBy, intersection, isEmpty, map, groupBy, get, omit } = require('lodash');

/**
 * Filters the list of store groups to those matching the set of attribute restrictions.
 *
 * @param {Array} storeGroupAttributeRestrictions - The list of attribute restrictions to filter on.
 * @param {Array} storeGroups - All store groups available.
 */
const getAccessibleStoreGroups = ({ storeGroupAttributeRestrictions = [], storeGroups }) => {
    // Need to map the attributes to the list of store groups.
    // This logic should live in a utils file which can be run on login.
    const accessibleStoreGroups = [];

    // Store group attribute restrictions is an array of distinct restrictions. The user
    // will have access to any store group satisfied by at least one of these restrictions.
    storeGroupAttributeRestrictions.forEach(attributeRestrictions => {
        // For each set of restrictions filter down the store groups
        // to return only the store groups keys which satisfy all restrictions.
        const filteredStoreGroups = storeGroups.filter(storeGroup => {
            return attributeRestrictions.every(attributeRestriction => {
                // A store group satisfies each restriction if it has
                // an attribute with the corresponding attribute name and a value
                // given in the attribute restriction.
                return storeGroup.attributes.some(
                    attribute =>
                        attribute.attributeName === attributeRestriction.attributeName &&
                        attributeRestriction.values.includes(attribute.value)
                );
            });
        });
        const filteredStoreGroupKeys = map(filteredStoreGroups, 'key');

        accessibleStoreGroups.push(...filteredStoreGroupKeys);
    });

    const uniqueStoreGroups = uniq(accessibleStoreGroups);
    return uniqueStoreGroups;
};

/**
 * Filters the store group attribute restrictions to remove any attributes or values which
 * are no longer valid options.
 *
 * @param {Array} storeGroupAttributeRestrictions - The list of attribute restrictions to filter.
 * @param {Array} storeGroupAttributeOptions - The list of store groups attribute options to filter with.
 */
const filterInactiveAttributes = ({
    storeGroupAttributeRestrictions = [],
    storeGroupAttributeOptions = [],
}) => {
    const filteredRestrictions = [];

    storeGroupAttributeRestrictions.forEach(restrictions => {
        const filteredRestriction = [];
        restrictions.forEach(restriction => {
            const attributeOptions = storeGroupAttributeOptions.find(
                options => options.attributeName === restriction.attributeName
            );

            // Restriction is for a valid attribute.
            // Now remove any options which are no longer valid.
            if (attributeOptions) {
                const filteredValues = intersection(
                    restriction.values,
                    map(attributeOptions.options, 'value')
                );

                if (filteredValues.length) {
                    filteredRestriction.push({
                        attributeName: restriction.attributeName,
                        values: filteredValues,
                    });
                }
            }
        });
        if (filteredRestriction.length) filteredRestrictions.push(filteredRestriction);
    });

    return filteredRestrictions;
};

/**
 * Generate a list of all attribute options for the supplied storeGroups.
 *
 * @param {Object} storeGroups - The list of store groups to retrieve the attributes from.
 */
const getStoreGroupAttributeOption = ({ storeGroups = [] }) => {
    // Identify all store group options available
    const storeGroupAttributes = storeGroups.flatMap(sg => sg.attributes);

    // Reduce this to a unique list of options based on the attribute name and value.
    const uniqueAttributes = uniqBy(
        storeGroupAttributes,
        attr => `${attr.attributeName}::${attr.value}`
    );

    // Group these based on their attribute
    const groupedAttributes = groupBy(uniqueAttributes, 'attributeName');

    // Finally return an array of objects containing each attributeName and their options.
    return Object.entries(groupedAttributes).map(([key, value]) => {
        return {
            attributeName: key,
            options: value,
        };
    });
};

/**
 * Validates and cleans user access by removing references to categories/storegroups
 * that no longer exist in the system.
 *
 * @param {Object} requestedAccess - { categories: [string], storeGroups: [number] }
 * @param {Array} availableCategories - All active category keys in the tenant
 * @param {Array} availableStoreGroups - All active store group keys in the tenant
 * @returns {Object} Cleaned access object with invalid references removed
 */
const validateAndCleanAccess = ({
    requestedAccess = {},
    availableCategories = [],
    availableStoreGroups = [],
}) => {
    const cleaned = {
        categories: [],
        storeGroups: [],
    };

    // Filter categories to only those that still exist
    if (requestedAccess.categories && Array.isArray(requestedAccess.categories)) {
        cleaned.categories = requestedAccess.categories.filter(cat =>
            availableCategories.includes(cat)
        );
    }

    // Filter storeGroups to only those that still exist
    if (requestedAccess.storeGroups && Array.isArray(requestedAccess.storeGroups)) {
        cleaned.storeGroups = requestedAccess.storeGroups.filter(sg =>
            availableStoreGroups.includes(sg)
        );
    }

    return cleaned;
};

/**
 * Reads the slice of a tenant-partitioned sub-document that applies to a single tenant.
 *
 * @param {Object} value - A sub-document such as `access` or `accessRestrictions`.
 * @param {string} [tenantKey] - The tenant to read. Omit for single-tenant deployments.
 * @param {Array<string>} tenantKeys - Tenant keys belonging to the profile.
 * @returns {Object} The tenant's slice of the sub-document.
 */
const getTenantScopedValue = ({ value = {}, tenantKey, tenantKeys = [] } = {}) => {
    const hasTenantPartitions = tenantKeys.some(key => Object.hasOwn(value || {}, key));

    if (!tenantKey) return omit(value || {}, tenantKeys);
    if (Object.hasOwn(value || {}, tenantKey)) return get(value, [tenantKey]) || {};
    if (hasTenantPartitions) return {};

    return omit(value || {}, tenantKeys);
};

/**
 * Produces a new tenant-partitioned sub-document with `tenantValue` applied to a single tenant,
 * leaving every other tenant's data untouched.
 *
 * @param {Object} value - The existing sub-document.
 * @param {string} [tenantKey] - The tenant to write. Omit for single-tenant deployments.
 * @param {Array<string>} tenantKeys - Tenant keys belonging to the profile.
 * @param {Object} tenantValue - The slice to store against the tenant.
 * @returns {Object} The updated sub-document.
 */
const setTenantScopedValue = ({
    value = {},
    tenantKey,
    tenantKeys = [],
    tenantValue = {},
} = {}) => {
    if (!tenantKey) return { ...omit(value || {}, tenantKeys), ...tenantValue };

    return {
        ...(value || {}),
        [tenantKey]: tenantValue,
    };
};

/**
 * Resolves the categories and store groups a user may access within a single tenant.
 *
 * @param {Object} access - The `access` sub-document of a user profile.
 * @param {string} [tenantKey] - The tenant to resolve. Omit for single-tenant deployments.
 * @param {Array<string>} tenantKeys - Tenant keys belonging to the profile.
 * @returns {Object} Access object { categories: [string], storeGroups: [number] }
 */
const getTenantAccess = ({ access = {}, tenantKey, tenantKeys = [] } = {}) => {
    const scopedAccess = getTenantScopedValue({ value: access, tenantKey, tenantKeys });

    return {
        categories: scopedAccess.categories || [],
        storeGroups: scopedAccess.storeGroups || [],
    };
};

/**
 * Produces a new `access` sub-document with the supplied access applied to a single tenant.
 *
 * @param {Object} access - The existing `access` sub-document of a user profile.
 * @param {string} [tenantKey] - The tenant to write. Omit for single-tenant deployments.
 * @param {Array<string>} tenantKeys - Tenant keys belonging to the profile.
 * @param {Object} tenantAccess - Access object { categories: [string], storeGroups: [number] }
 * @returns {Object} The updated `access` sub-document.
 */
const setTenantAccess = ({ access = {}, tenantKey, tenantKeys = [], tenantAccess = {} } = {}) =>
    setTenantScopedValue({
        value: access,
        tenantKey,
        tenantKeys,
        tenantValue: {
            categories: tenantAccess.categories || [],
            storeGroups: tenantAccess.storeGroups || [],
        },
    });

/**
 * Determines whether a user profile is administrable from within a tenant.
 * Profiles without a tenant list predate multitenancy and remain visible everywhere.
 *
 * @param {Object} profile - The user profile.
 * @param {string} [tenantKey] - The tenant of the current request.
 * @returns {boolean} True when the profile belongs to the tenant.
 */
const isProfileInTenant = (profile, tenantKey) => {
    if (!tenantKey) return true;

    const tenants = get(profile, 'tenants');

    return isEmpty(tenants) || tenants.includes(tenantKey);
};

/**
 * Adds a tenant to a user's tenant list.
 *
 * @param {Array<string>} tenants - The existing tenant list.
 * @param {string} tenantKey - The tenant to add.
 * @returns {Array<string>} The tenant list including `tenantKey`.
 */
const addTenant = (tenants, tenantKey) => uniq([...(tenants || []), tenantKey]);

module.exports = {
    getAccessibleStoreGroups,
    filterInactiveAttributes,
    getStoreGroupAttributeOption,
    validateAndCleanAccess,
    getTenantScopedValue,
    setTenantScopedValue,
    getTenantAccess,
    setTenantAccess,
    isProfileInTenant,
    addTenant,
};
